import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Globe, Link2, RefreshCw, Settings, AlertTriangle, Check, Clock, ArrowUpRight, Plus, Trash2, Calendar, Building2 } from 'lucide-react';
import api from '@/lib/api';

export default function DistributionPage() {
  const [channels, setChannels] = useState([]);
  const [providers, setProviders] = useState([]);
  const [restrictions, setRestrictions] = useState([]);
  const [channelBookings, setChannelBookings] = useState([]);
  const [syncLogs, setSyncLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [showAddChannel, setShowAddChannel] = useState(false);
  const [showAddRestriction, setShowAddRestriction] = useState(false);
  
  // Form states
  const [newChannel, setNewChannel] = useState({
    provider_id: '',
    channel_name: '',
    channel_code: '',
    hotel_code: '',
    commission_rate: 0
  });
  
  const [newRestriction, setNewRestriction] = useState({
    channel_id: '',
    restriction_type: 'stop_sell',
    start_date: '',
    end_date: ''
  });

  // Get property_id from localStorage
  // Get property_id from auth context
  const getSelectedPropertyId = () => {
    const storedProperty = localStorage.getItem('hms_current_property');
    if (storedProperty) {
      try {
        const prop = JSON.parse(storedProperty);
        return prop.id || '';
      } catch {
        return '';
      }
    }
    return '';
  };
  
  const selectedProperty = getSelectedPropertyId();

  useEffect(() => {
    if (selectedProperty) {
      loadData();
    }
  }, [selectedProperty]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [channelsRes, providersRes, statsRes] = await Promise.all([
        api.get(`/distribution/channels?property_id=${selectedProperty}`),
        api.get('/distribution/providers'),
        api.get(`/distribution/stats?property_id=${selectedProperty}`)
      ]);
      
      setChannels(channelsRes.data || []);
      setProviders(providersRes.data || []);
      setStats(statsRes.data);
      
      // Load restrictions
      const restrictionsRes = await api.get(`/distribution/restrictions?property_id=${selectedProperty}`);
      setRestrictions(restrictionsRes.data || []);
      
      // Load channel bookings
      const bookingsRes = await api.get(`/distribution/bookings?property_id=${selectedProperty}`);
      setChannelBookings(bookingsRes.data || []);
      
      // Load sync logs
      const logsRes = await api.get(`/distribution/sync-logs?property_id=${selectedProperty}`);
      setSyncLogs(logsRes.data || []);
    } catch (error) {
      console.error('Error loading distribution data:', error);
      toast.error('Failed to load distribution data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddChannel = async () => {
    try {
      await api.post(`/distribution/channels?property_id=${selectedProperty}`, newChannel);
      toast.success('Channel added successfully');
      setShowAddChannel(false);
      setNewChannel({ provider_id: '', channel_name: '', channel_code: '', hotel_code: '', commission_rate: 0 });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add channel');
    }
  };

  const handleToggleChannel = async (channelId, currentStatus) => {
    try {
      await api.put(`/distribution/channels/${channelId}?is_active=${!currentStatus}`);
      toast.success(`Channel ${!currentStatus ? 'activated' : 'deactivated'}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update channel');
    }
  };

  const handleTriggerSync = async (channelId, syncType) => {
    try {
      await api.post(`/distribution/channels/${channelId}/sync?sync_type=${syncType}`);
      toast.success(`${syncType} sync triggered`);
      loadData();
    } catch (error) {
      toast.error('Failed to trigger sync');
    }
  };

  const handleAddRestriction = async () => {
    try {
      await api.post('/distribution/restrictions', newRestriction);
      toast.success('Restriction added successfully');
      setShowAddRestriction(false);
      setNewRestriction({ channel_id: '', restriction_type: 'stop_sell', start_date: '', end_date: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add restriction');
    }
  };

  const handleDeleteRestriction = async (restrictionId) => {
    if (!window.confirm('Are you sure you want to delete this restriction?')) return;
    try {
      await api.delete(`/distribution/restrictions/${restrictionId}`);
      toast.success('Restriction deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete restriction');
    }
  };

  const handleImportBooking = async (bookingId) => {
    try {
      const res = await api.post(`/distribution/bookings/${bookingId}/import`);
      toast.success(`Booking imported: ${res.data.hms_confirmation_number}`);
      loadData();
    } catch (error) {
      toast.error('Failed to import booking');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      success: 'bg-emerald-100 text-emerald-800',
      failed: 'bg-red-100 text-red-800',
      partial: 'bg-amber-100 text-amber-800',
      new: 'bg-blue-100 text-blue-800',
      imported: 'bg-emerald-100 text-emerald-800'
    };
    return <Badge className={variants[status] || 'bg-slate-100 text-slate-800'}>{status}</Badge>;
  };

  if (!selectedProperty) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-10 text-center">
            <Building2 className="mx-auto h-12 w-12 text-slate-400 mb-4" />
            <h3 className="text-lg font-semibold">No Property Selected</h3>
            <p className="text-slate-500">Please select a property from the dashboard to manage distribution channels.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="distribution-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Distribution & Channels</h1>
          <p className="text-slate-500">Manage OTA connections, channel mappings, and restrictions</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Dialog open={showAddChannel} onOpenChange={setShowAddChannel}>
            <DialogTrigger asChild>
              <Button data-testid="add-channel-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Channel
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Channel Connection</DialogTitle>
                <DialogDescription>Connect to an OTA or channel manager</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Channel Provider</Label>
                  <Select value={newChannel.provider_id} onValueChange={(v) => setNewChannel({...newChannel, provider_id: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent>
                      {providers.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Channel Name</Label>
                  <Input value={newChannel.channel_name} onChange={(e) => setNewChannel({...newChannel, channel_name: e.target.value})} placeholder="e.g., Booking.com" />
                </div>
                <div>
                  <Label>Channel Code</Label>
                  <Input value={newChannel.channel_code} onChange={(e) => setNewChannel({...newChannel, channel_code: e.target.value})} placeholder="e.g., BOOKINGCOM" />
                </div>
                <div>
                  <Label>Hotel Code (in Channel)</Label>
                  <Input value={newChannel.hotel_code} onChange={(e) => setNewChannel({...newChannel, hotel_code: e.target.value})} placeholder="Your hotel ID in the channel" />
                </div>
                <div>
                  <Label>Commission Rate (%)</Label>
                  <Input type="number" value={newChannel.commission_rate} onChange={(e) => setNewChannel({...newChannel, commission_rate: parseFloat(e.target.value) || 0})} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddChannel(false)}>Cancel</Button>
                <Button onClick={handleAddChannel}>Add Channel</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Active Channels</p>
                <p className="text-2xl font-bold">{stats?.active_channels || 0}</p>
              </div>
              <Globe className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Channels</p>
                <p className="text-2xl font-bold">{stats?.total_channels || 0}</p>
              </div>
              <Link2 className="h-8 w-8 text-indigo-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pending Bookings</p>
                <p className="text-2xl font-bold">{channelBookings.filter(b => b.booking_status === 'new').length}</p>
              </div>
              <Clock className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Active Restrictions</p>
                <p className="text-2xl font-bold">{restrictions.filter(r => r.is_active).length}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="channels" className="space-y-4">
        <TabsList>
          <TabsTrigger value="channels">Channels</TabsTrigger>
          <TabsTrigger value="restrictions">Restrictions</TabsTrigger>
          <TabsTrigger value="bookings">Channel Bookings</TabsTrigger>
          <TabsTrigger value="logs">Sync Logs</TabsTrigger>
        </TabsList>

        {/* Channels Tab */}
        <TabsContent value="channels">
          <Card>
            <CardHeader>
              <CardTitle>Connected Channels</CardTitle>
              <CardDescription>Manage your OTA and distribution channel connections</CardDescription>
            </CardHeader>
            <CardContent>
              {channels.length === 0 ? (
                <div className="text-center py-10 text-slate-500">
                  <Globe className="mx-auto h-12 w-12 mb-4 opacity-50" />
                  <p>No channels connected yet</p>
                  <p className="text-sm">Click "Add Channel" to connect to an OTA</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Channel</TableHead>
                      <TableHead>Provider</TableHead>
                      <TableHead>Hotel Code</TableHead>
                      <TableHead>Commission</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Last Sync</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {channels.map(channel => (
                      <TableRow key={channel.id} data-testid={`channel-row-${channel.id}`}>
                        <TableCell className="font-medium">{channel.channel_name}</TableCell>
                        <TableCell>{channel.channel_providers?.name || '-'}</TableCell>
                        <TableCell>{channel.hotel_code || '-'}</TableCell>
                        <TableCell>{channel.commission_rate}%</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Switch checked={channel.is_active} onCheckedChange={() => handleToggleChannel(channel.id, channel.is_active)} />
                            <span className={channel.is_active ? 'text-emerald-600' : 'text-slate-400'}>
                              {channel.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {channel.last_availability_push ? new Date(channel.last_availability_push).toLocaleString() : 'Never'}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" onClick={() => handleTriggerSync(channel.id, 'availability')} title="Push Availability">
                              <ArrowUpRight className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleTriggerSync(channel.id, 'bookings')} title="Pull Bookings">
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setSelectedChannel(channel)}>
                              <Settings className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Restrictions Tab */}
        <TabsContent value="restrictions">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Channel Restrictions</CardTitle>
                <CardDescription>Manage stop-sell, CTA, CTD, and other restrictions per channel</CardDescription>
              </div>
              <Dialog open={showAddRestriction} onOpenChange={setShowAddRestriction}>
                <DialogTrigger asChild>
                  <Button size="sm" data-testid="add-restriction-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Restriction
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Channel Restriction</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label>Channel</Label>
                      <Select value={newRestriction.channel_id} onValueChange={(v) => setNewRestriction({...newRestriction, channel_id: v})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select channel" />
                        </SelectTrigger>
                        <SelectContent>
                          {channels.map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.channel_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Restriction Type</Label>
                      <Select value={newRestriction.restriction_type} onValueChange={(v) => setNewRestriction({...newRestriction, restriction_type: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="stop_sell">Stop Sell</SelectItem>
                          <SelectItem value="cta">Close to Arrival</SelectItem>
                          <SelectItem value="ctd">Close to Departure</SelectItem>
                          <SelectItem value="min_los">Minimum LOS</SelectItem>
                          <SelectItem value="max_los">Maximum LOS</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Start Date</Label>
                        <Input type="date" value={newRestriction.start_date} onChange={(e) => setNewRestriction({...newRestriction, start_date: e.target.value})} />
                      </div>
                      <div>
                        <Label>End Date</Label>
                        <Input type="date" value={newRestriction.end_date} onChange={(e) => setNewRestriction({...newRestriction, end_date: e.target.value})} />
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowAddRestriction(false)}>Cancel</Button>
                    <Button onClick={handleAddRestriction}>Add Restriction</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {restrictions.length === 0 ? (
                <div className="text-center py-10 text-slate-500">
                  <AlertTriangle className="mx-auto h-12 w-12 mb-4 opacity-50" />
                  <p>No restrictions configured</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Channel</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Room Type</TableHead>
                      <TableHead>Start Date</TableHead>
                      <TableHead>End Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {restrictions.map(r => (
                      <TableRow key={r.id}>
                        <TableCell>{r.channels?.channel_name || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{r.restriction_type.replace('_', ' ').toUpperCase()}</Badge>
                        </TableCell>
                        <TableCell>{r.room_types?.name || 'All'}</TableCell>
                        <TableCell>{r.start_date}</TableCell>
                        <TableCell>{r.end_date}</TableCell>
                        <TableCell>
                          {r.is_active ? <Badge className="bg-emerald-100 text-emerald-800">Active</Badge> : <Badge variant="secondary">Inactive</Badge>}
                        </TableCell>
                        <TableCell>
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteRestriction(r.id)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Channel Bookings Tab */}
        <TabsContent value="bookings">
          <Card>
            <CardHeader>
              <CardTitle>Channel Bookings</CardTitle>
              <CardDescription>Bookings received from OTAs pending import</CardDescription>
            </CardHeader>
            <CardContent>
              {channelBookings.length === 0 ? (
                <div className="text-center py-10 text-slate-500">
                  <Calendar className="mx-auto h-12 w-12 mb-4 opacity-50" />
                  <p>No channel bookings</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Channel</TableHead>
                      <TableHead>Confirmation #</TableHead>
                      <TableHead>Guest</TableHead>
                      <TableHead>Check-in</TableHead>
                      <TableHead>Check-out</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {channelBookings.map(booking => (
                      <TableRow key={booking.id}>
                        <TableCell>{booking.channels?.channel_name || '-'}</TableCell>
                        <TableCell className="font-mono">{booking.channel_confirmation_number}</TableCell>
                        <TableCell>{booking.guest_name}</TableCell>
                        <TableCell>{booking.check_in_date}</TableCell>
                        <TableCell>{booking.check_out_date}</TableCell>
                        <TableCell>{booking.total_amount?.toLocaleString()} {booking.currency_code}</TableCell>
                        <TableCell>{getStatusBadge(booking.booking_status)}</TableCell>
                        <TableCell>
                          {booking.booking_status === 'new' && (
                            <Button size="sm" onClick={() => handleImportBooking(booking.id)}>
                              <Check className="h-4 w-4 mr-1" /> Import
                            </Button>
                          )}
                          {booking.hms_confirmation_number && (
                            <span className="text-sm text-slate-500">{booking.hms_confirmation_number}</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sync Logs Tab */}
        <TabsContent value="logs">
          <Card>
            <CardHeader>
              <CardTitle>Sync Logs</CardTitle>
              <CardDescription>Recent synchronization activity</CardDescription>
            </CardHeader>
            <CardContent>
              {syncLogs.length === 0 ? (
                <div className="text-center py-10 text-slate-500">
                  <RefreshCw className="mx-auto h-12 w-12 mb-4 opacity-50" />
                  <p>No sync logs yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Channel</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Sent</TableHead>
                      <TableHead>Received</TableHead>
                      <TableHead>Failed</TableHead>
                      <TableHead>Time</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {syncLogs.map(log => (
                      <TableRow key={log.id}>
                        <TableCell>{log.channels?.channel_name || '-'}</TableCell>
                        <TableCell><Badge variant="outline">{log.sync_type}</Badge></TableCell>
                        <TableCell>{getStatusBadge(log.sync_status)}</TableCell>
                        <TableCell>{log.records_sent}</TableCell>
                        <TableCell>{log.records_received}</TableCell>
                        <TableCell className={log.records_failed > 0 ? 'text-red-600' : ''}>{log.records_failed}</TableCell>
                        <TableCell>{log.completed_at ? new Date(log.completed_at).toLocaleString() : '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
